import PageContainer from "@/pages/containers/page-container";
import SpendSummaryForNetwork from "@/pages/components/spend-summary/spend-summary-network";
import { useSpendSummaryWithCoverage } from "@/hooks/use-spend-summary";
import CoverageSelect from "@/pages/components/spend-summary/coverage-select";
import type { SpendSummaryNetwork } from "@/model/spend-summary";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import SpendSummarySkeleton from "./components/spend-summary/spend-summary-skeleton";
import useUserInfo from "@/hooks/use-user-info";
import { NoDataAvailable, SomethingWentWrong } from "@/pages/components/empty";
import { useSelector } from "react-redux";
import type { RootState } from "@/store/store";
import { useUserDataQueries } from "@/hooks/use-login";
import { useEffect, useState } from "react";
import { useFeatureName } from "@/hooks/use-pageTitle";
import { COVERAGE_CODE, QueryKeys } from "@/constants";
import { queryClient } from "@/hooks/use-query-client";
import { DEFAULT_NETWORK_VALUES } from "@/constants/ui_labels";
import type { IUiLabelsResponse } from "@/model/ui-labels";
import { getErrorMessage } from "@/lib/error-message-utils";
import { isRbpClient } from "@/lib/client-utils";

type SpendSummaryProps = {
  shouldWrap?: boolean;
  useMedCoverage?: boolean;
};

const SpendSummary: React.FunctionComponent<SpendSummaryProps> = ({
  shouldWrap = true,
  useMedCoverage = false,
}) => {
  const [selectedTab, setSelectedTab] = useState("tab1");
  const [showDisclaimer, setShowDisclaimer] = useState<boolean>(true);

  const { activeCoverage, selectedCoverageYear, coveragePlanDates } =
    useSelector((state: RootState) => state.planCoverageInfo);

  const userInfo = useUserInfo();
  const featureName = useFeatureName();
  const { spendQuery } = useSpendSummaryWithCoverage(
    userInfo,
    useMedCoverage
      ? userInfo?.planCoverageInfo.find(
          (coverage) => coverage?.claimCoverageCode === COVERAGE_CODE.MEDICAL,
        )
      : activeCoverage,
    useMedCoverage
      ? coveragePlanDates.find((item) => item.type === COVERAGE_CODE.MEDICAL)
          ?.periods?.[0]
      : selectedCoverageYear,
  );

  const { globalFlagsQuery } = useUserDataQueries({
    groupId: userInfo?.groupId || "",
    divisionId: userInfo?.divisionId || "",
    accessToken: userInfo?.accessToken || "",
    username: userInfo?.username || "",
  });
  const networkUiLabels = queryClient.getQueryData<
    IUiLabelsResponse | undefined
  >([QueryKeys.UI_LABELS])?.data?.network;

  useEffect(() => {
    setSelectedTab("tab1");
  }, [activeCoverage.claimCoverageCode]);

  const renderDisclaimer = () => {
    if (!showDisclaimer || !spendQuery?.data?.header) return null;

    const shouldShowPlanDate =
      Boolean(selectedCoverageYear?.isAccumYear) ||
      (Boolean(coveragePlanDates) && coveragePlanDates.length === 0);

    return (
      <div className="m-4 flex items-start bg-red-50 p-4">
        <span className="flex flex-col gap-y-4 text-sm font-normal text-red-500">
          {spendQuery.data.header.message}
          {shouldShowPlanDate && (
            <span className="text-xs text-red-500">
              {`Plan Date: ${spendQuery.data.header.planStartDate} - ${spendQuery.data.header.planEndDate}`}
            </span>
          )}
          {Boolean(spendQuery.data.header.additionalMessage) && (
            <span className="text-xs text-red-500">
              {spendQuery.data.header.additionalMessage}
            </span>
          )}
        </span>

        <button
          type="button"
          onClick={() => setShowDisclaimer(false)}
          className="ml-auto text-xs font-semibold text-red-500"
        >
          Dismiss
        </button>
      </div>
    );
  };

  const renderTabsBar = () => (
    <div className="tabs-list-container mb-3 flex flex-col gap-y-4 pb-1 @[640px]:flex-row">
      <TabsList className="tabs-list" style={{ padding: 0 }}>
        {Boolean(globalFlagsQuery?.data?.showNetworkTabs) &&
          !isRbpClient(userInfo?.memberId) &&
          Object.values(networkUiLabels || DEFAULT_NETWORK_VALUES).map(
            (networkItem: string, index) => {
              const value = index + 1;

              if (
                value > 1 &&
                activeCoverage.claimCoverageCode === COVERAGE_CODE.DENTAL
              )
                return;

              return (
                <TabsTrigger
                  key={`tab${value}`}
                  className="tabs-trigger"
                  value={`tab${value}`}
                >
                  {networkItem}
                </TabsTrigger>
              );
            },
          )}
      </TabsList>

      {Boolean(coveragePlanDates) && coveragePlanDates?.length > 0 && (
        <CoverageSelect disableDropdown={useMedCoverage} />
      )}
    </div>
  );

  const renderSpendSummaryContent = () => {
    if (spendQuery?.isLoading) {
      return <SpendSummarySkeleton />;
    }

    if (spendQuery.isError) {
      const errorMessage = getErrorMessage(spendQuery.error);
      return <SomethingWentWrong message={errorMessage} />;
    }

    if (spendQuery.data?.error || spendQuery.data?.data?.length === 0) {
      return (
        <>
          {activeCoverage?.claimCoverageCode === COVERAGE_CODE.VISION ||
          activeCoverage?.claimCoverageCode === COVERAGE_CODE.ORTHODONTIA
            ? renderDisclaimer()
            : null}
          <NoDataAvailable />
        </>
      );
    }

    return (
      <>
        {renderDisclaimer()}
        {spendQuery?.data?.data?.map(
          (item: SpendSummaryNetwork, index: number) => {
            const value = index + 1;

            return (
              <TabsContent
                key={item?.networkType + selectedCoverageYear?.id}
                value={`tab${value}`}
                className="tabs-content"
              >
                <SpendSummaryForNetwork
                  networkData={item}
                  // Defaults to false when the API omits the config key.
                  showFamilyDentalOop={
                    spendQuery.data?.config?.showFamilyDentalOop ?? false
                  }
                />
              </TabsContent>
            );
          },
        )}
      </>
    );
  };

  const renderSpendSummaryLayout = () => (
    <Tabs
      value={selectedTab}
      onValueChange={setSelectedTab}
      className="tabs @container"
    >
      {renderTabsBar()}
      {renderSpendSummaryContent()}
    </Tabs>
  );

  return shouldWrap ? (
    <PageContainer
      title={`${featureName} ${userInfo.planCoverageInfo.length === 1 ? `(${activeCoverage.claimCoverageCodeDesc})` : ""}`}
    >
      {renderSpendSummaryLayout()}
    </PageContainer>
  ) : (
    <>{renderSpendSummaryLayout()}</>
  );
};

export default SpendSummary;
